import React, { useEffect, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Avatar,
  HStack,
  Text,
  useToast,
  Select,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Grid,
  GridItem,
  useBreakpointValue,
  useColorModeValue,
} from '@chakra-ui/react';
import { type UserProfile, updateUserProfile } from '../services/userService';
import { uploadFileDirect } from '../services/uploadService';
import { useAuth } from '../contexts/AuthContext';

interface EditProfileDialogProps {
  isOpen: boolean;
  onClose: () => void;
  initialProfile: UserProfile | null;
  onSaved?: (profile: Partial<UserProfile>) => void;
}

const EditProfileDialog: React.FC<EditProfileDialogProps> = ({ isOpen, onClose, initialProfile, onSaved }) => {
  const toast = useToast();
  const { user } = useAuth();
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [bio, setBio] = useState('');
  const [language, setLanguage] = useState('');
  const [location, setLocation] = useState('');
  const [age, setAge] = useState<number>(18);
  const [gender, setGender] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Responsive breakpoints
  const modalSize = useBreakpointValue({ base: 'full', md: 'xl' });
  
  // Color mode values
  const bgColor = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.800', 'white');
  const inputBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  useEffect(() => {
    setName(initialProfile?.name || '');
    setUsername(initialProfile?.username || '');
    setBio(initialProfile?.bio || '');
    setLanguage(initialProfile?.language || '');
    setLocation(initialProfile?.location || '');
    setAge(initialProfile?.age || 18);
    setGender(initialProfile?.gender || '');
    setPhoneNumber(initialProfile?.phoneNumber || '');
    setAvatarFile(null);
    setAvatarPreview(null);
  }, [initialProfile, isOpen]);

  const handleSave = async () => {
    if (!user) {
      toast({ title: 'Please sign in', status: 'warning' });
      return;
    }
    setSaving(true);
    try {
      const updates: Partial<UserProfile> = { 
        name, 
        username, 
        bio, 
        language, 
        location, 
        age, 
        gender, 
        phoneNumber
      };
      if (avatarFile) {
        const url = await uploadFileDirect(`users/${user.uid}/avatar`, avatarFile);
        updates.avatarUrl = url;
      }
      await updateUserProfile(user.uid, updates);
      onSaved?.(updates);
      toast({ title: 'Profile updated', status: 'success' });
      onClose();
    } catch (e: any) {
      toast({ title: 'Update failed', description: e.message, status: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size={modalSize}>
      <ModalOverlay />
      <ModalContent bg={bgColor} color={textColor} maxH={{ base: '100vh', md: '90vh' }} m={{ base: 0, md: 4 }}>
        <ModalHeader fontSize={{ base: 'lg', md: 'xl' }} px={{ base: 4, md: 6 }} py={{ base: 3, md: 4 }}>
          Edit Profile
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody px={{ base: 4, md: 6 }} py={{ base: 4, md: 4 }} maxH={{ base: 'calc(100vh - 120px)', md: '70vh' }} overflowY="auto">
          <VStack align="stretch" gap={{ base: 4, md: 4 }}>
            <VStack gap={4} align="center" pt={{ base: 6, md: 4 }}>
              <Avatar 
                size={{ base: 'xl', md: 'lg' }} 
                src={avatarPreview || initialProfile?.avatarUrl} 
                name={name} 
              />
              <VStack gap={2} align="center">
                <Button 
                  as="label" 
                  htmlFor="avatar-input" 
                  variant="outline" 
                  size={{ base: 'sm', md: 'md' }}
                  w={{ base: 'full', md: 'auto' }}
                  maxW={{ base: '200px', md: 'none' }}
                  colorScheme={avatarFile ? 'green' : 'gray'}
                >
                  {avatarFile ? 'Avatar Selected ✓' : 'Change Avatar'}
                </Button>
                {avatarFile && (
                  <Text fontSize="xs" color="green.500" fontWeight="medium">
                    {avatarFile.name} - Ready to upload
                  </Text>
                )}
              </VStack>
              <Input 
                id="avatar-input" 
                type="file" 
                accept="image/*" 
                display="none" 
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  setAvatarFile(file);
                  if (file) {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                      setAvatarPreview(reader.result as string);
                    };
                    reader.readAsDataURL(file);
                  } else {
                    setAvatarPreview(null);
                  }
                }} 
              />
            </VStack>

            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={{ base: 3, md: 4 }}>
              <GridItem>
                <FormControl>
                  <FormLabel fontSize={{ base: 'sm', md: 'md' }} color={textColor}>Name</FormLabel>
                  <Input 
                    value={name} 
                    onChange={(e) => setName(e.target.value)} 
                    bg={inputBg}
                    borderColor={borderColor}
                    _focus={{ borderColor: 'purple.400' }}
                    size={{ base: 'sm', md: 'md' }}
                  />
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl>
                  <FormLabel fontSize={{ base: 'sm', md: 'md' }} color={textColor}>Username</FormLabel>
                  <Input 
                    value={username} 
                    onChange={(e) => setUsername(e.target.value)} 
                    bg={inputBg}
                    borderColor={borderColor}
                    _focus={{ borderColor: 'purple.400' }}
                    size={{ base: 'sm', md: 'md' }}
                  />
                </FormControl>
              </GridItem>
            </Grid>

            <FormControl>
              <FormLabel fontSize={{ base: 'sm', md: 'md' }} color={textColor}>Bio</FormLabel>
              <Textarea 
                rows={3} 
                value={bio} 
                onChange={(e) => setBio(e.target.value)} 
                bg={inputBg}
                borderColor={borderColor}
                _focus={{ borderColor: 'purple.400' }}
                size={{ base: 'sm', md: 'md' }}
                resize="vertical"
              />
            </FormControl>

            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={{ base: 3, md: 4 }}>
              <GridItem>
                <FormControl>
                  <FormLabel fontSize={{ base: 'sm', md: 'md' }} color={textColor}>Language</FormLabel>
                  <Select 
                    value={language} 
                    onChange={(e) => setLanguage(e.target.value)} 
                    placeholder="Select language"
                    bg={inputBg}
                    borderColor={borderColor}
                    _focus={{ borderColor: 'purple.400' }}
                    size={{ base: 'sm', md: 'md' }}
                  >
                    <option value="en">English</option>
                    <option value="es">Spanish</option>
                    <option value="fr">French</option>
                    <option value="de">German</option>
                    <option value="it">Italian</option>
                    <option value="pt">Portuguese</option>
                    <option value="ru">Russian</option>
                    <option value="ja">Japanese</option>
                    <option value="ko">Korean</option>
                    <option value="zh">Chinese</option>
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl>
                  <FormLabel fontSize={{ base: 'sm', md: 'md' }} color={textColor}>Location</FormLabel>
                  <Input 
                    value={location} 
                    onChange={(e) => setLocation(e.target.value)} 
                    placeholder="City, Country" 
                    bg={inputBg}
                    borderColor={borderColor}
                    _focus={{ borderColor: 'purple.400' }}
                    size={{ base: 'sm', md: 'md' }}
                  />
                </FormControl>
              </GridItem>
            </Grid>

            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={{ base: 3, md: 4 }}>
              <GridItem>
                <FormControl>
                  <FormLabel fontSize={{ base: 'sm', md: 'md' }} color={textColor}>Age</FormLabel>
                  <NumberInput 
                    value={age} 
                    onChange={(_, value) => setAge(value)} 
                    min={13} 
                    max={120}
                    size={{ base: 'sm', md: 'md' }}
                  >
                    <NumberInputField 
                      bg={inputBg}
                      borderColor={borderColor}
                      _focus={{ borderColor: 'purple.400' }}
                    />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl>
                  <FormLabel fontSize={{ base: 'sm', md: 'md' }} color={textColor}>Gender</FormLabel>
                  <Select 
                    value={gender} 
                    onChange={(e) => setGender(e.target.value)} 
                    placeholder="Select gender"
                    bg={inputBg}
                    borderColor={borderColor}
                    _focus={{ borderColor: 'purple.400' }}
                    size={{ base: 'sm', md: 'md' }}
                  >
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="non-binary">Non-binary</option>
                    <option value="other">Other</option>
                    <option value="prefer-not-to-say">Prefer not to say</option>
                  </Select>
                </FormControl>
              </GridItem>
            </Grid>

            <FormControl>
              <FormLabel fontSize={{ base: 'sm', md: 'md' }} color={textColor}>Phone Number</FormLabel>
              <Input 
                value={phoneNumber} 
                onChange={(e) => setPhoneNumber(e.target.value)} 
                placeholder="+1 (555) 123-4567" 
                bg={inputBg}
                borderColor={borderColor}
                _focus={{ borderColor: 'purple.400' }}
                size={{ base: 'sm', md: 'md' }}
              />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter px={{ base: 4, md: 6 }} py={{ base: 3, md: 4 }}>
          <HStack w="full" justify={{ base: 'stretch', md: 'flex-end' }} gap={3}>
            <Button 
              variant="ghost" 
              onClick={onClose}
              size={{ base: 'sm', md: 'md' }}
              w={{ base: 'full', md: 'auto' }}
            >
              Cancel
            </Button>
            <Button 
              colorScheme="purple" 
              onClick={handleSave} 
              isLoading={saving}
              size={{ base: 'sm', md: 'md' }}
              w={{ base: 'full', md: 'auto' }}
            >
              Save
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EditProfileDialog;


